import type {Highlight, Property} from "~/models/old/Property";
import {useFormData} from "~/composables/useFormData";
import type {AxiosResponse} from "axios";
import type {CVCard} from "~/interfaces/CVCard";
import type {ListingProperty} from "~/models/services/listing/Listing";
import {LISTINGS_SERVICE} from "~/data/ServicesConstants";

const getPropertyFromLS = (key: string): Property | null => {
    if (process.client) {
        const property_raw = localStorage.getItem(key)
        if (!!property_raw) {
            return JSON.parse(property_raw)
        }
    }
    return null
}

const saveProperty = async (property: Property): Promise<AxiosResponse> => {
    let form: FormData = useFormData().objectToFormData(property)
    return (await useNuxtApp().$getApi(LISTINGS_SERVICE)).post('/listings/properties', form, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    })
}
const updateProperty = async (property: Property): Promise<AxiosResponse> => {
    let form: FormData = useFormData().objectToFormData(property)
    let i = 0;
    for (const index in property.pictures) {
        //@ts-ignore
        const el = property.pictures[index]

        if (typeof el == 'string') {
            form.append('picture_order[' + i + ']', el)
        } else {
            form.append('picture_order[' + i + ']', (el as File).name)
        }
        i += 1
    }
    const axiosRequest = await useNuxtApp().$getApi(LISTINGS_SERVICE);
    return axiosRequest.postForm(`/listings/properties/${property._id}`, form)
}

const destroyProperty = async (property: Property): Promise<AxiosResponse> => {
    const axiosRequest = await useNuxtApp().$getApi(LISTINGS_SERVICE);
    return axiosRequest.delete(`/listings/properties/${property._id ?? property.id}`)
}
const getAllProperties = async (params?: any) => {
    const axiosRequest = await useNuxtApp().$getApi(LISTINGS_SERVICE);
    return axiosRequest.get('/listings/public/properties', {
        params: params ?? {}
    })
}
const getUserProperties = async (): Promise<AxiosResponse> => {
    const axiosRequest = await useNuxtApp().$getApi(LISTINGS_SERVICE);
    return axiosRequest.get('/listings/properties')
}

const transformPropertyToCard = (property: Property): CVCard => {
    return {
        _id: property._id!,
        link: property.link!,
        price: property.price!,
        title: property.info!.title!,
        created_at: property.created_at ?? new Date(),
        isAdvertisement: !!property.is_ad,
        status: property.availability ?? '',
        pictures: property.pictures as Array<string>,
        address: `${property.address?.street}, ${property.address?.state}, ${property.address?.zip}`,
        type: 'property',
        hasLike: (property.followers?.length ?? 0) > 0,
        resume: property.resume,
        daysLeft: property.days_in_cv,
        agent: {
            name: `${property.agent?.name ?? property.user_full_name}`,
            _id: property.agent?._id ?? property.user_id,
            type: property.agent ? 'agent' : 'user',
            email: property.agent?.email,
        },
        extras: {
            area: `${property.area?.value} ${("" + property.area?.units).replaceAll('2', '')}`,
            rooms: `${property.bedrooms}`,
            bathrooms: `${property.bathrooms}`,
            type: property.type ?? '',
            availability: property.availability ?? '',
        },
    }
}
const transformListingPropertyToCard = (property: ListingProperty): CVCard => {
    const address = !!property.address ? (typeof property.address == 'string' ? property.address : useAddress().getFullAddress(property.address)) : ''
    return {
        _id: `${property.id}`,
        link: property.link!,
        price: property.price!,
        title: property.title!,
        created_at: property.created_at ?? new Date(),
        isAdvertisement: !!property.advertisement,
        status: property.sub_category,
        pictures: property.pictures,
        address: address,
        type: 'property',
        hasLike: false,
        resume: '',
        daysLeft: property.daysLeft,
        agent: {
            name: property.profile?.name ?? 'Usuario',
            _id: `${property.user_id}`,
            type: 'user',
            email: '',
        },
        extras: {
            area: `${property.property_area_value} ${("" + property.property_area_unit).replaceAll('2', '')}`,
            rooms: `${property.property_bedrooms}`,
            bathrooms: `${property.property_bathrooms}`,
            type: propertyType.find(el => el.value == property.category)?.option ?? '',
            availability: property.sub_category,
        },
    }
}
const transformArrayPropertyToCard = (property: Property[]): CVCard[] => {
    return property.map(el => (transformPropertyToCard(el)))
}

export const useProperty = () => ({
    propertyType,
    propertyHighlights,
    getPropertyFromLS,
    saveProperty,
    getUserProperties,
    getAllProperties,
    updateProperty,
    destroyProperty,
    transformPropertyToCard,
    transformArrayPropertyToCard,
    transformListingPropertyToCard
})

const propertyType = [
    {option: 'Casa', value: 'HOUSE'},
    {option: 'Terreno/Lote', value: 'LAND_LOT'},
    {option: 'Apartamento/Condominio', value: 'APT_CONDO'},
    {option: 'Comercial', value: 'COMMERCIAL'},
]
const propertyHighlights: Highlight[] = [
    {value: "Lavandería", icon: "fa-solid fa-jug-detergent"},
    {value: "Den/Oficina", icon: "fa-solid fa-briefcase"},
    {value: "A/C o A/C Central", icon: "fa-solid fa-wind"},
    {value: "Vista", icon: "fa-solid fa-eye"},
    {value: "Balcón", icon: "fa-solid fa-building"},
    {value: "Electrodomésticos", icon: "fa-solid fa-kitchen-set"},
    {value: "Generador", icon: "fa-solid fa-bolt"},
    {value: "Cisterna", icon: "fa-solid fa-water"},
    {value: "Seguridad", icon: "fa-solid fa-shield-alt"},
    {value: "Conserje", icon: "fa-solid fa-concierge-bell"},
    {value: "Ascensor", icon: "fa-solid fa-elevator"},
    {value: "Estacionamientos", icon: "fa-solid fa-parking"},
    {value: "Piscina", icon: "fa-solid fa-swimming-pool"},
    {value: "Áreas recreativas", icon: "fa-solid fa-child"},
    {value: "Gimnasio", icon: "fa-solid fa-dumbbell"},
    {value: "Salón de actividades", icon: "fa-solid fa-users"},
    {value: "Garajes", icon: "fa-solid fa-warehouse"},
    {value: "Jardinería", icon: "fa-solid fa-tree"},
    {value: "Terraza", icon: "fa-solid fa-umbrella-beach"},
    {value: "Control de acceso", icon: "fa-solid fa-lock"},
];
